import type { BuildData } from './types';

type BuildRoutes = Record<string, string>;

type CachedBuildRoutes = {
  timestamp: number;
  data: BuildRoutes;
};

export const BUILD_NOT_FOUND = 'BUILD_NOT_FOUND';

export const ATLAS_SCENE_NAME = 'Atlas';

const ROUTES_CACHE_PREFIX = 'atlas-build-routes';
const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
const RETRY_DELAYS_MS = [1000, 2000] as const;
const buildsMemoryCache = new Map<string, BuildRoutes>();

const unityBuildDomain = (): string => {
  return import.meta.env.VITE_UNITY_BUILD;
};

export const buildResolver = async (sceneName: string): Promise<BuildData> => {
  const buildPath = await resolveBuildPath({ sceneName });

  if (buildPath === BUILD_NOT_FOUND) {
    const message = `Exception: ${BUILD_NOT_FOUND} trying to resolve build for scene: ${sceneName}`;
    // manageError(message); TO DO
    return {
      buildPath: BUILD_NOT_FOUND,
    };
  }

  return {
    buildPath: buildPath.replace('Build/index.html', ''),
    buildDomain: unityBuildDomain(),
  };
};

const isApplet = (sceneName: string): boolean => {
  return sceneName !== ATLAS_SCENE_NAME;
};

const getCacheKey = (stagePrefix: string): string =>
  `${ROUTES_CACHE_PREFIX}:${stagePrefix}`;

const getLocalStorageInstance = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const loadCachedBuildRoutes = (key: string): CachedBuildRoutes | null => {
  const storage = getLocalStorageInstance();

  if (!storage) {
    return null;
  }

  const rawValue = storage.getItem(key);

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as CachedBuildRoutes;
  } catch {
    storage.removeItem(key);
    return null;
  }
};

const persistBuildRoutes = ({
  key,
  data,
}: {
  key: string;
  data: BuildRoutes;
}): void => {
  const storage = getLocalStorageInstance();

  if (!storage) {
    return;
  }

  const payload: CachedBuildRoutes = {
    timestamp: Date.now(),
    data,
  };

  try {
    storage.setItem(key, JSON.stringify(payload));
  } catch {
    storage.removeItem(key);
  }
};

const isCacheFresh = (timestamp: number): boolean => {
  return Date.now() - timestamp < ONE_DAY_IN_MS;
};

const wait = ({ duration }: { duration: number }): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, duration);
  });
};

const fetchBuildRoutes = async ({
  url,
}: {
  url: string;
}): Promise<BuildRoutes> => {
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      return (await response.json()) as BuildRoutes;
    } catch (error) {
      if (attempt === RETRY_DELAYS_MS.length) {
        throw error;
      }

      await wait({ duration: RETRY_DELAYS_MS[attempt] });
    }
  }

  throw new Error(`Error fetching routes.json. ${url}`);
};

const resolveRoutes = async ({
  stagePrefix,
}: {
  stagePrefix: string;
}): Promise<BuildRoutes> => {
  const cacheKey = getCacheKey(stagePrefix);
  const memoryCacheEntry = buildsMemoryCache.get(cacheKey);

  if (memoryCacheEntry) {
    return memoryCacheEntry;
  }

  const cachedEntry = loadCachedBuildRoutes(cacheKey);

  if (cachedEntry && isCacheFresh(cachedEntry.timestamp)) {
    buildsMemoryCache.set(cacheKey, cachedEntry.data);
    return cachedEntry.data;
  }

  const routesUrl = `${unityBuildDomain()}/${stagePrefix}/BuildRoutes/routes.json`;

  try {
    const fetchedBuildRoutes = await fetchBuildRoutes({ url: routesUrl });
    buildsMemoryCache.set(cacheKey, fetchedBuildRoutes);
    persistBuildRoutes({ key: cacheKey, data: fetchedBuildRoutes });
    return fetchedBuildRoutes;
  } catch (error) {
    if (cachedEntry?.data) {
      buildsMemoryCache.set(cacheKey, cachedEntry.data);
      console.warn(`Fetch failed for ${routesUrl}`, error);
      return cachedEntry.data;
    }

    const errorInstance =
      error instanceof Error
        ? error
        : new Error(`Error fetching routes.json. ${routesUrl}`);
    console.error(errorInstance);
    throw errorInstance;
  }
};

const resolveBuildPath = async ({
  sceneName,
}: {
  sceneName: string;
}): Promise<string> => {
  const stagePrefix = isApplet(sceneName) ? 'Primary' : 'WebApp';
  const routes = await resolveRoutes({ stagePrefix });
  const routedBuildPath = routes[sceneName];

  if (!routedBuildPath) {
    return BUILD_NOT_FOUND;
  }

  return `${stagePrefix}${routedBuildPath}`;
};
